<template>
  <TColorPicker
    :default-color="currentColor"
    :colors="colors"
    :inverse="inverse"
    :light="!colorPickerDark"
    v-tooltip="{ content: 'Background color' }"
    @color-change="setColor"
  >
    <template #buttonIcon>
      <svg
        fill="#000000"
        width="20px"
        height="20px"
        viewBox="0 0 36 36"
        version="1.1"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
      >
        <path
          d="M15.82,26.06a1,1,0,0,1-.71-.29L8.67,19.33a1,1,0,0,1-.29-.71,1,1,0,0,1,.29-.71L23,3.54a5.55,5.55,0,1,1,7.85,7.86L16.53,25.77A1,1,0,0,1,15.82,26.06Zm-5-7.44,5,5L29.48,10a3.54,3.54,0,0,0,0-5,3.63,3.63,0,0,0-5,0Z"
          fill="currentColor"
        ></path>
        <path
          d="M10.38,28.28A1,1,0,0,1,9.67,28L6.45,24.77a1,1,0,0,1-.22-1.09l2.22-5.44a1,1,0,0,1,1.63-.33l6.45,6.44A1,1,0,0,1,16.2,26l-5.44,2.22A1.33,1.33,0,0,1,10.38,28.28ZM8.33,23.82l2.29,2.28,3.43-1.4L9.74,20.39Z"
          fill="currentColor"
        ></path>
        <path
          d="M8.94,30h-5a1,1,0,0,1-.84-1.55l3.22-4.94a1,1,0,0,1,1.55-.16l3.21,3.22a1,1,0,0,1,.06,1.35L9.7,29.64A1,1,0,0,1,8.94,30ZM5.78,28H8.47L9,27.34l-1.7-1.7Z"
          fill="currentColor"
        ></path>
        <rect
          x="3.06"
          y="31"
          width="30"
          height="3"
          :fill="currentColor || '#000000'"
        ></rect>
        <rect x="0" y="0" width="36" height="36" fill-opacity="0" />
      </svg>
    </template>
  </TColorPicker>
</template>

<script lang="ts" setup>
import {
  expandSelectionToWord,
  getActiveMarks,
} from "../_internal/cutomExtensions";
import { TColorPicker } from "@topol/twig-ui";
import { Editor } from "@tiptap/core";
import { computed, ref, watch } from "vue";

const props = defineProps<{
  editor: Editor;
  colors?: string[];
  inverse?: boolean;
  colorPickerDark?: boolean;
}>();

const localColor = ref<string | null>(null);
const previousColor = ref<string>("");

const editorColor = computed(() => {
  setPreviousColor();
  if (!props.editor) return "";

  // Get marks including stored marks (for empty paragraphs after Enter)
  const marks = getActiveMarks(props.editor);
  const textStyle = marks.find((mark) => mark.type.name === "textStyle");
  // A `var(...)` reference (a consumer can remap a picked color to one, to
  // keep it linked to a live theme value) isn't a color the picker's own
  // RGB/HSV math can parse for its swatch preview -- fall through to the DOM
  // fallback below instead, which reads the browser's already-resolved value.
  if (
    textStyle?.attrs?.backgroundColor &&
    !textStyle.attrs.backgroundColor.startsWith("var(")
  ) {
    return textStyle.attrs.backgroundColor;
  }

  const { state } = props.editor;
  const { $from } = state.selection;

  const nodeFont = $from.parent.attrs?.backgroundColor;
  if (nodeFont && !nodeFont.startsWith("var(")) {
    return nodeFont;
  }

  // 3️⃣ Fallback: zjisti z DOM
  if (typeof window !== "undefined" && window.getSelection) {
    const selection = window.getSelection();
    if (selection && selection.anchorNode) {
      let node = selection.anchorNode as HTMLElement | null;

      if (node?.classList?.contains("tiptap") && node.firstElementChild) {
        node = node.firstElementChild as HTMLElement;
      }

      // Najdi rodiče, co je prvek
      while (node && node.nodeType !== Node.ELEMENT_NODE) {
        node = node.parentElement;
      }

      let parentEditor = node;
      let isInEditor = false;
      while (parentEditor) {
        if (parentEditor.classList.contains("tiptap-editor")) {
          isInEditor = true;
          break;
        }
        parentEditor = parentEditor.parentElement as HTMLElement | null;
      }

      if (node && isInEditor) {
        const style = window.getComputedStyle(node);
        return style.backgroundColor || "";
      }
    }
  }
  return previousColor.value;
});

const currentColor = computed(() => localColor.value ?? editorColor.value);

function setColor(color: string) {
  if (!props.editor) return;
  localColor.value = color;
  expandSelectionToWord(props.editor);
  // Apply to the editor's preserved selection WITHOUT calling .focus():
  // .focus() moves DOM focus back into the contenteditable, which steals
  // focus from the toolbar's hex input on every keystroke (TOPOL-tiptap bug).
  props.editor.chain().setBackgroundColor(color).run();
}

function setPreviousColor() {
  if (editorColor.value) previousColor.value = editorColor.value;
}

watch(
  () => props.editor.state,
  () => {
    if (!props.editor.isFocused) return;
    if (
      localColor.value &&
      !props.editor.isActive("textStyle", {
        backgroundColor: localColor.value,
      })
    ) {
      localColor.value = null;
    }
  },
);
</script>
